import React, { useState, useEffect, useCallback } from "react";
import {
    HiOutlineClock,
    HiOutlineShare,
    HiOutlineDocumentText,
    HiOutlineFolder,
    HiOutlineSearch,
    HiOutlineCalendar,
    HiOutlineChevronLeft,
    HiOutlineChevronRight,
    HiOutlineFilter,
    HiOutlineCloudDownload,
    HiOutlineKey,
    HiOutlineRefresh,
    HiOutlineTrash,
    HiOutlineXCircle,
    HiOutlineArrowUp,
    HiOutlineArrowDown,
    HiOutlineChartBar
} from "react-icons/hi";
import { motion } from "framer-motion";
import { toast } from "react-toastify";
import { useActivity } from "../ActivityContext";
import { getUsersAPI } from "../services/driveService";

const ActivityPage = () => {
    const {
        activities,
        loadingActivities: loading,
        totalRecords,
        totalPages,
        fetchActivityLogs,
        reportData,
        loadingReport,
        refreshActivityReport
    } = useActivity();

    // Filter & Pagination states
    const [searchQuery, setSearchQuery] = useState("");
    const [startDate, setStartDate] = useState("");
    const [endDate, setEndDate] = useState("");
    const [page, setPage] = useState(1);
    const [users, setUsers] = useState([]);
    const [selectedUserId, setSelectedUserId] = useState("");
    const pageSize = 15;

    useEffect(() => {
        const fetchUsers = async () => {
            try {
                const response = await getUsersAPI();
                setUsers(response?.data || []);
            } catch (error) {
                console.error("Failed to fetch users", error);
            }
        };
        fetchUsers();
    }, []);

    const getMetricMeta = (type) => {
        switch (type) {
            case "FILE_UPLOADED":
            case "FILE_UPLOAD": return { icon: <HiOutlineArrowUp />, color: "text-blue-600", bg: "bg-blue-50", label: "File Uploaded" };
            case "FOLDER_CREATED":
            case "FOLDER_CREATE": return { icon: <HiOutlineFolder />, color: "text-amber-600", bg: "bg-amber-50", label: "Folder Created" };
            case "FILE_DOWNLOADED": return { icon: <HiOutlineArrowDown />, color: "text-emerald-600", bg: "bg-emerald-50", label: "File Downloaded" };
            case "DOWNLOAD_TOKEN_REQUESTED": return { icon: <HiOutlineKey />, color: "text-indigo-600", bg: "bg-indigo-50", label: "Token Requests" };
            case "ACCESS_GRANTED": return { icon: <HiOutlineShare />, color: "text-purple-600", bg: "bg-purple-50", label: "Shares" };
            case "FOLDER_RECOVERED": return { icon: <HiOutlineRefresh />, color: "text-emerald-600", bg: "bg-emerald-50", label: "Folder Recovered" };
            case "FILE_RECOVERED": return { icon: <HiOutlineRefresh />, color: "text-emerald-600", bg: "bg-emerald-50", label: "File Recovered" };
            case "FOLDER_TRASHED": return { icon: <HiOutlineTrash />, color: "text-rose-600", bg: "bg-rose-50", label: "Folder Trashed" };
            case "FILE_TRASHED": return { icon: <HiOutlineTrash />, color: "text-rose-600", bg: "bg-rose-50", label: "File Trashed" };
            case "FOLDER_DELETED": return { icon: <HiOutlineTrash />, color: "text-red-600", bg: "bg-red-50", label: "Folder Deleted" };
            case "FILE_DELETED":
            case "FILE_DELETE": return { icon: <HiOutlineTrash />, color: "text-red-600", bg: "bg-red-50", label: "File Deleted" };
            case "FILE_DELETED_PERMANENTLY": return { icon: <HiOutlineXCircle />, color: "text-red-600", bg: "bg-red-50", label: "Permanently Deleted" };
            default: return { icon: <HiOutlineClock />, color: "text-slate-500", bg: "bg-slate-50", label: type.replace(/_/g, ' ') };
        }
    };

    const fetchActivities = useCallback(async () => {
        try {
            const params = {
                page,
                pageSize,
                searchQuery,
            };

            if (startDate) params.startDate = new Date(startDate).toISOString();
            if (endDate) params.endDate = new Date(endDate).toISOString();

            // Use selected user ID if present, otherwise pass empty string to filter
            const filterUserId = selectedUserId || '';
            params.filterUserId = filterUserId;

            await Promise.all([
                fetchActivityLogs(params),
                refreshActivityReport(filterUserId)
            ]);
        } catch (error) {
            toast.error("Failed to fetch activity logs");
        }
    }, [page, searchQuery, startDate, endDate, selectedUserId, fetchActivityLogs]);

    // useEffect(() => {
    //     const fetchData = async () => {
    //         const { data } = await axios.get(`${server}/activity/getAll`, {
    //             headers: {
    //                 Authorization: `Bearer ${localStorage.getItem("dbToken")}`
    //             }
    //         })
    //         console.log(data);
    //     }
    //     fetchData()
    // }, [])

    useEffect(() => {
        const timeoutId = setTimeout(() => {
            fetchActivities();
        }, 500); // Debounce search
        return () => clearTimeout(timeoutId);
    }, [fetchActivities]);

    const getActionIcon = (type) => {
        switch (type) {
            case "ACCESS_GRANTED": return <HiOutlineShare className="text-indigo-500" size={16} />;
            case "FILE_UPLOAD":
            case "FILE_UPLOADED": return <HiOutlineArrowUp className="text-blue-500" size={16} />;
            case "FOLDER_CREATE":
            case "FOLDER_CREATED": return <HiOutlineFolder className="text-amber-500" size={16} />;
            case "FILE_DOWNLOADED": return <HiOutlineArrowDown className="text-emerald-500" size={16} />;
            case "DOWNLOAD_TOKEN_REQUESTED": return <HiOutlineKey className="text-slate-400" size={14} />;
            case "FOLDER_RECOVERED":
            case "FILE_RECOVERED": return <HiOutlineRefresh className="text-emerald-500" size={16} />;
            case "FOLDER_TRASHED":
            case "FILE_TRASHED": return <HiOutlineTrash className="text-yellow-600" size={16} />;
            case "FOLDER_DELETED":
            case "FILE_DELETED":
            case "FOLDER_DELETE":
            case "FILE_DELETE": return <HiOutlineTrash className="text-red-500" size={16} />;
            default: return <HiOutlineClock className="text-slate-400" size={16} />;
        }
    };

    const formatDate = (dateString) => {
        const date = new Date(dateString);
        return date.toLocaleString('en-US', {
            month: 'short', day: 'numeric', year: 'numeric',
            hour: '2-digit', minute: '2-digit'
        });
    };

    return (
        <div className="space-y-4 animate-in fade-in slide-in-from-bottom-4 duration-700">
            {/* Header Section - More Compact */}
            <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 px-2">
                <div>
                    <h1 className="text-3xl font-extrabold text-slate-900 tracking-tight">Activity Master</h1>
                    <p className="text-slate-400 text-[10px] font-bold uppercase tracking-[0.2em] mt-1">Unified history & real-time operation logs</p>
                </div>

                {/* Search Bar - Slimmer */}
                <div className="relative group min-w-[280px]">
                    <div className="absolute inset-y-0 left-0 pl-3.5 flex items-center pointer-events-none text-slate-400">
                        <HiOutlineSearch size={16} />
                    </div>
                    <input
                        type="text"
                        placeholder="Quick search..."
                        value={searchQuery}
                        onChange={(e) => { setSearchQuery(e.target.value); setPage(1); }}
                        className="w-full pl-10 pr-4 py-2.5 bg-white border border-slate-200/60 rounded-xl focus:outline-none focus:ring-4 focus:ring-indigo-50 focus:border-indigo-600 transition-all text-xs font-bold text-slate-900"
                    />
                </div>
            </div>

            {/* History Summary Grid - Adaptive columns */}
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-3 px-2">
                {loadingReport ? (
                    Array(5).fill(0).map((_, i) => (
                        <div key={i} className="h-16 bg-white animate-pulse rounded-2xl border border-slate-100" />
                    ))
                ) : (
                    <>
                        {(() => {
                            // Aggregate data by label for minimal cards
                            const aggregated = {};
                            reportData?.reportData?.forEach(item => {
                                const meta = getMetricMeta(item.actionType);
                                if (!aggregated[meta.label]) {
                                    aggregated[meta.label] = { ...meta, count: 0 };
                                }
                                aggregated[meta.label].count += item.count;
                            });

                            return Object.values(aggregated)
                                .sort((a, b) => b.count - a.count)
                                .map((item, idx) => (
                                    <motion.div
                                        key={item.label}
                                        initial={{ opacity: 0, y: 10 }}
                                        animate={{ opacity: 1, y: 0 }}
                                        transition={{ delay: idx * 0.05 }}
                                        className="bg-white p-3 rounded-2xl border border-slate-200/60 shadow-sm flex items-center gap-3 hover:shadow-md transition-all cursor-default group"
                                    >
                                        <div className={`w-8 h-8 rounded-lg ${item.bg} ${item.color} flex items-center justify-center text-sm group-hover:scale-110 transition-transform`}>
                                            {item.icon}
                                        </div>
                                        <div className="flex flex-col min-w-0">
                                            <span className="text-[8px] font-black uppercase tracking-widest text-slate-400 truncate">{item.label}</span>
                                            <span className="text-sm font-black text-slate-900 leading-none">{item.count}</span>
                                        </div>
                                    </motion.div>
                                ));
                        })()}
                    </>
                )}
            </div>

            {/* Filter Bar - Streamlined */}
            <div className="flex flex-wrap items-center gap-3 bg-white p-2 px-4 rounded-xl border border-slate-200/60 shadow-sm">
                <div className="flex items-center gap-2 pr-3 border-r border-slate-100">
                    <HiOutlineFilter size={14} className="text-indigo-600" />
                    <span className="text-[9px] font-black uppercase tracking-widest text-slate-400">Filters</span>
                </div>

                <div className="flex items-center gap-2">
                    <input
                        type="date"
                        value={startDate}
                        onChange={(e) => { setStartDate(e.target.value); setPage(1); }}
                        className="px-3 py-1.5 bg-slate-50 border border-slate-100 rounded-lg text-[10px] font-bold text-slate-600 focus:outline-none focus:border-indigo-600 transition-all"
                    />
                    <span className="text-slate-300 font-bold text-[10px] uppercase">to</span>
                    <input
                        type="date"
                        value={endDate}
                        onChange={(e) => { setEndDate(e.target.value); setPage(1); }}
                        className="px-3 py-1.5 bg-slate-50 border border-slate-100 rounded-lg text-[10px] font-bold text-slate-600 focus:outline-none focus:border-indigo-600 transition-all"
                    />
                </div>

                <div className="flex items-center gap-2 pl-3 border-l border-slate-100">
                    <span className="text-[9px] font-black uppercase tracking-widest text-slate-400">User</span>
                    <select
                        value={selectedUserId}
                        onChange={(e) => { setSelectedUserId(e.target.value); setPage(1); }}
                        className="px-3 py-1.5 bg-slate-50 border border-slate-100 rounded-lg text-[10px] font-bold text-slate-600 focus:outline-none focus:border-indigo-600 transition-all appearance-none pr-8 relative"
                        style={{ backgroundImage: 'url("data:image/svg+xml,%3Csvg xmlns=\'http://www.w3.org/2000/svg\' fill=\'none\' viewBox=\'0 0 24 24\' stroke=\'%236366f1\'%3E%3Cpath stroke-linecap=\'round\' stroke-linejoin=\'round\' stroke-width=\'2\' d=\'M19 9l-7 7-7-7\'%3E%3C/path%3E%3C/svg%3E")', backgroundRepeat: 'no-repeat', backgroundPosition: 'right 8px center', backgroundSize: '12px' }}
                    >
                        <option value="">All Users</option>
                        {users.map(u => (
                            <option key={u.id} value={u.id}>{u.fullName}</option>
                        ))}
                    </select>
                </div>

                {(searchQuery || startDate || endDate || selectedUserId) && (
                    <button
                        onClick={() => { setSearchQuery(""); setStartDate(""); setEndDate(""); setSelectedUserId(""); setPage(1); }}
                        className="ml-auto text-[9px] font-black uppercase tracking-widest text-rose-500 hover:text-rose-700 transition-colors"
                    >
                        Clear
                    </button>
                )}
            </div>

            {/* Content Section - Table Format with horizontal scroll */}
            <div className="bg-white rounded-2xl border border-slate-200/60 shadow-xl overflow-x-auto fancy-scrollbar min-h-[500px] flex flex-col">
                <div className="min-w-[700px] flex flex-col flex-1">
                    {/* Table Header */}
                    {!loading && activities.length > 0 && (
                        <div className="flex items-center px-6 py-3 border-b border-slate-200 text-[9px] font-black uppercase tracking-[0.2em] text-slate-400 bg-slate-50/50 sticky top-0 z-10 backdrop-blur-md">
                            <div className="flex-1 flex items-center gap-3">
                                <span className="w-8"></span> {/* Icon Spacer */}
                                <span>Activity Details</span>
                            </div>
                            <div className="w-48">Performed By</div>
                            <div className="w-32">Action Type</div>
                            <div className="w-40 text-right">Timestamp</div>
                        </div>
                    )}

                    {loading ? (
                        <div className="flex-1 flex flex-col items-center justify-center gap-4 text-slate-400 py-32">
                            <div className="w-8 h-8 border-3 border-indigo-100 border-t-indigo-600 rounded-full animate-spin" />
                            <p className="font-bold tracking-widest text-[9px] uppercase italic">Retrieving logs...</p>
                        </div>
                    ) : activities.length === 0 ? (
                        <div className="flex-1 flex flex-col items-center justify-center gap-4 text-slate-400 py-32">
                            <HiOutlineClock size={48} className="opacity-10" />
                            <p className="font-bold tracking-widest text-[9px] uppercase">No matching activity records</p>
                        </div>
                    ) : (
                        <div className="divide-y divide-slate-100/50 flex-1">
                            {activities.map((activity, idx) => (
                                <motion.div
                                    initial={{ opacity: 0, y: 5 }}
                                    animate={{ opacity: 1, y: 0 }}
                                    transition={{ delay: idx * 0.02 }}
                                    key={activity.id}
                                    className="px-6 py-1.5 hover:bg-slate-50 transition-all flex items-center group cursor-default"
                                >
                                    <div className="flex-1 flex items-center gap-3 min-w-0">
                                        <div className="w-8 h-8 rounded-lg bg-slate-100 flex items-center justify-center transition-all group-hover:scale-105 group-hover:bg-white group-hover:shadow-sm">
                                            {getActionIcon(activity.actionType)}
                                        </div>
                                        <h3 className="font-bold text-slate-800 text-[12px] truncate pr-4" title={activity.details}>
                                            {activity.details}
                                        </h3>
                                    </div>

                                    <div className="w-48">
                                        <div className="flex items-center gap-2">
                                            <div className="w-5 h-5 rounded-full bg-indigo-50 border border-indigo-100 flex items-center justify-center text-[8px] font-black text-indigo-600 uppercase">
                                                {activity.user.fullName.charAt(0)}
                                            </div>
                                            <span className="text-[11px] font-bold text-slate-600 truncate">{activity.user.fullName}</span>
                                        </div>
                                    </div>

                                    <div className="w-32">
                                        <div className="flex items-center gap-1.5 px-2.5 py-1 rounded-md bg-slate-50 text-slate-500 border border-slate-200/50">
                                            <div className="opacity-70">{getActionIcon(activity.actionType)}</div>
                                            <span className="text-[9px] font-black uppercase tracking-wider truncate">
                                                {activity.actionType.replace(/_/g, ' ')}
                                            </span>
                                        </div>
                                    </div>

                                    <div className="w-40 text-right flex flex-col">
                                        <span className="text-[11px] font-bold text-slate-700">{formatDate(activity.createdAt).split(' at ')[1]}</span>
                                        <span className="text-[9px] font-bold uppercase tracking-widest text-slate-400">{formatDate(activity.createdAt).split(' at ')[0]}</span>
                                    </div>
                                </motion.div>
                            ))}
                        </div>
                    )}

                    {/* Pagination Footer */}
                    <div className="p-6 bg-slate-50/50 border-t border-slate-100 flex flex-col sm:flex-row items-center justify-between gap-4">
                        <div className="flex flex-col gap-1">
                            <span className="text-[10px] font-black uppercase tracking-widest text-slate-400">
                                Page <span className="text-slate-900">{page}</span> of <span className="text-slate-900">{totalPages}</span>
                            </span>
                            <span className="text-[9px] font-bold uppercase tracking-[0.1em] text-slate-300">
                                Showing <span className="text-slate-500">{activities.length > 0 ? (page - 1) * pageSize + 1 : 0}</span> - <span className="text-slate-500">{Math.min(page * pageSize, totalRecords)}</span> of <span className="text-slate-500">{totalRecords}</span> records
                            </span>
                        </div>
                        <div className="flex items-center gap-2">
                            <button
                                disabled={page === 1 || loading}
                                onClick={() => { setPage(p => Math.max(1, p - 1)); window.scrollTo(0, 0); }}
                                className="p-3 bg-white border border-slate-200 rounded-xl text-slate-500 hover:text-indigo-600 hover:border-indigo-600 disabled:opacity-30 disabled:cursor-not-allowed transition-all shadow-sm group/btn"
                                title="Previous Page"
                            >
                                <HiOutlineChevronLeft size={20} className="group-hover/btn:-translate-x-0.5 transition-transform" />
                            </button>
                            <button
                                disabled={page === totalPages || loading}
                                onClick={() => { setPage(p => Math.min(totalPages, p + 1)); window.scrollTo(0, 0); }}
                                className="p-3 bg-white border border-slate-200 rounded-xl text-slate-500 hover:text-indigo-600 hover:border-indigo-600 disabled:opacity-30 disabled:cursor-not-allowed transition-all shadow-sm group/btn"
                                title="Next Page"
                            >
                                <HiOutlineChevronRight size={20} className="group-hover/btn:translate-x-0.5 transition-transform" />
                            </button>
                        </div>
                    </div>
                </div>
            </div>

            <div className="pb-12 text-center">
                <p className="text-[10px] font-bold text-slate-300 uppercase tracking-widest">Activity statistics updated in real-time</p>
            </div>
        </div>
    );
};

export default ActivityPage;
